import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { dropoffProviderType } from "../dropoff/dropoff-provider";
import { ExtendedWarrantySchema } from "../extended-warranty";
import { PriceSetSchema } from "../money/price-set";
import { FieldId, InputPartType } from "../return-flow";
import { BundleDataSchema } from "../returns/bundle-data";
import { OriginAddressSchema } from "../shopify-order";
import { ExchangeGroup } from "../team";

export const DEFAULT_VARIANT_TITLE = "Default Title";

export const ALL_DEFAULT_VARIANT_TITLES = [
  DEFAULT_VARIANT_TITLE,
  "Title",
  "none",
  "",
];

const NORMALIZED_DEFAULT_VARIANT_TITLES = ALL_DEFAULT_VARIANT_TITLES.map(
  (title) => title.toLowerCase().trim(),
);

export function isDefaultVariantTitle(title: string): boolean {
  const cleanedValue = title.trim().toLowerCase();
  return NORMALIZED_DEFAULT_VARIANT_TITLES.includes(cleanedValue);
}

//A similar enum called the same name exists in product model, but it is lowercase. This used to be a union-type of uppercase.
export enum InventoryPolicy {
  CONTINUE = "CONTINUE",
  DENY = "DENY",
}
export const exchangeVariantSchema_ = z.object({
  id: z.string(),
  sku: z.string().nullish(),
  productId: z.string().nullish(),
  productTitle: z.string().nullish(),
  title: z.string(),
  displayName: z.string(),
  price: z.string(),
  image: z.string().nullish(),
  inventoryQuantity: z.number(),
  inventoryTracked: z.boolean(),
  inventoryPolicy: createEnum(InventoryPolicy),
  selectedOptions: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
      productAsVariant: z.boolean().nullish(),
    }),
  ),
  backorder: z.string().nullish(),
});
export const exchangeVariantSchema: z.ZodType<ExchangeVariant> =
  exchangeVariantSchema_;
export interface ExchangeVariant
  extends z.infer<typeof exchangeVariantSchema_> {}

export const NewExchangeItemDiscountSchema = z.object({
  value: z.number(),
  valueType: createEnum(["PERCENTAGE", "FIXED_AMOUNT"]),
});
export type NewExchangeItemDiscount = z.infer<
  typeof NewExchangeItemDiscountSchema
>;

const AdvancedExchangeItemSchema_ = z.object({
  variantId: z.string(),
  productId: z.string(),
  title: z.string(),
  variantTitle: z.string(),
  /** @deprecated use priceSet instead */
  price: z.string(),
  priceSet: PriceSetSchema.nullish(),
  /** @deprecated use itemValueSet instead */
  itemValue: z.string().nullish(),
  itemValueSet: PriceSetSchema.nullish(),
  quantity: z.number(),
  images: z.array(z.string()),
  currencyCode: z.string(),
  attributes: z.record(z.string(), z.any()).nullish(),
  tax: z.string().nullish(),
  appliedDiscount: NewExchangeItemDiscountSchema.nullish(),
});
export const AdvancedExchangeItemSchema: z.ZodType<AdvancedExchangeItem> =
  AdvancedExchangeItemSchema_;
export type AdvancedExchangeItem = z.infer<typeof AdvancedExchangeItemSchema_>;

export const NewExchangeItemSchema = z.object({
  variantId: z.string(),
  productId: z.string().nullish(),
  quantity: z.number(),
  properties: z.record(z.string(), z.any()).nullish(),
  appliedDiscount: NewExchangeItemDiscountSchema.nullish(),
});

export type NewExchangeItem = z.infer<typeof NewExchangeItemSchema>;

export enum PriceDifference {
  IGNORE = "ignore",
  APPLY = "apply",
}
const baseExchangeGroupSchema = z.object({
  type: createEnum([
    "tag:tag",
    "tag:collection",
    "name:collection",
    "collection:collection",
  ]),
  groupName: z.string(),
  priceDifference: createEnum(["ignore", "apply"]),
});

const collectionInfoSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  kind: createEnum(["smart", "custom"]),
});
const collectionInfoSchema: z.ZodType<CollectionInfo> = collectionInfoSchema_;
export interface CollectionInfo extends z.infer<typeof collectionInfoSchema_> {}

export const exchangeGroupSchema = z.union([
  baseExchangeGroupSchema.extend({
    type: z.literal("tag:tag"),
    sourceTags: z.array(z.string()),
    targetTags: z.array(z.string()),
  }),
  baseExchangeGroupSchema.extend({
    type: z.literal("tag:collection"),
    sourceTags: z.array(z.string()),
    targetCollection: collectionInfoSchema,
  }),
  baseExchangeGroupSchema.extend({
    type: z.literal("name:collection"),
    sourceName: z.string(),
    targetCollection: collectionInfoSchema,
  }),
  baseExchangeGroupSchema.extend({
    type: z.literal("collection:collection"),
    sourceCollection: collectionInfoSchema,
    targetCollection: collectionInfoSchema,
  }),
]);

export const _: z.infer<typeof exchangeGroupSchema> = {} as ExchangeGroup;

const ResetSchema = z.object({
  step: z.number(),
  returnFlow: z.any().nullish(),
});

const discountNameSchema_ = z.object({ title: z.string(), code: z.string() });
export const discountNameSchema: z.ZodType<DiscountName> = discountNameSchema_;
export interface DiscountName extends z.infer<typeof discountNameSchema_> {}

export const ReturnProductSchema_ = z.object({
  id: z.string(),
  title: z.string(),
  variantTitle: z.string(),
  vendor: z.string().nullish(),
  variantId: z.string(),
  reset: ResetSchema.nullish(),
  /** @deprecated use images instead */
  image: z.string(),
  /**
   * This includes all images for the product, including variant images.
   * If it's not defined, use image as fallback. It will always be defined for Shopify.
   */
  images: z
    .array(z.object({ url: z.string(), alt: z.string().nullish() }))
    .nullish(),
  price: z.string(),
  percentOfBundle: z.number().nullish(),
  bundleData: BundleDataSchema.nullish(),
  tax: z.string(),
  currency: z.string(),
  productTags: z.array(z.string()),
  returnValue: z.string(),
  // The return value of returnable items can change depending on which products
  // are selected (e.g. if the bogo redistribution feature is enabled),
  // the pre-select return value is calculated before any products are selected
  preSelectedReturnValue: z.string().nullish(),
  bogoDiscountId: z.string().nullish(),
  sku: z.string(),
  deliveredOn: z.date(),
  fulfilledOn: z.date(),
  grams: z.number(),
  lastFulfillmentUpdateOn: z.date(),
  shipmentLocationId: z.string().nullish(),
  productId: z.string(),
  collections: z.array(z.string()),
  orderTimeCollections: z.array(z.string()).nullish(),
  productDiscounts: z.array(discountNameSchema),
  productDiscountedAmount: z.number(),
  loyaltyAmount: z.number().nullish(),
  productProperties: z.record(z.string(), z.any().nullable()).nullish(),
  productType: z.string(),
  productVariantMetafields: z.record(z.string(), z.any()).nullish(),
  variantData: z.object({
    productTitle: z.string(),
    tags: z.array(z.string()),
    options: z.array(
      z.object({
        position: z.number(),
        name: z.string(),
        values: z.array(z.string()),
      }),
    ),
    variants: z.array(exchangeVariantSchema),
  }),
  fulfillmentOrigin: OriginAddressSchema.nullish(),
  isUnbundled: z.boolean().nullish(),
  isExpandedPrimaryProduct: z.boolean().nullish(),
  originalPrice: z.string().nullish(),
  taxRate: z.number().nullish(),
  quantity: z.number().nullish(),
  extendedWarranty: ExtendedWarrantySchema.nullish(),
  parentVariantId: z.string().nullish(),
});
export const ReturnProductSchema: z.ZodType<ReturnProduct> =
  ReturnProductSchema_;
export interface ReturnProduct extends z.infer<typeof ReturnProductSchema_> {}

const ReturnableItemSchema_ = z.object({
  id: z.string(),
  lineItemId: z.string().nullish(),
  primaryProductId: z.string(),
  value: z.string().nullish(),
  products: z.array(ReturnProductSchema),
  isExpanded: z.boolean(),
  rules: z.any().nullish(), // todo
  orderId: z.string(),
  selected: z.optional(z.boolean().nullish()),
  isTnplContractLineItem: z.optional(z.boolean().nullish()),
  parentReturnValue: z.string().nullish(),
});
export const ReturnableItemSchema: z.ZodType<ReturnableItem> =
  ReturnableItemSchema_;
export interface ReturnableItem extends z.infer<typeof ReturnableItemSchema_> {}

const NonReturnableItemSchema_ = z.object({
  lineItemId: z.string(),
  orderId: z.string().optional(),
  reasons: z.array(z.string()),
  title: z.string(),
  variantTitle: z.string().nullish(),
  image: z.string().nullish(),
  price: z.string(),
  currency: z.string(),
  quantity: z.number(),
  variantId: z.string().nullish(),
});
export const NonReturnableItemSchema: z.ZodType<NonReturnableItem> =
  NonReturnableItemSchema_;
export interface NonReturnableItem
  extends z.infer<typeof NonReturnableItemSchema_> {}

const VariantExchangeItemSchema_ = z.object({
  variantId: z.string().nullish(),
  productId: z.string().nullish(),
  preserveProperties: z.boolean(),
  priceDifference: createEnum(["ignore", "apply"]).nullish(),
  title: z.string(),
  image: z.string().nullish(),
  productTitle: z.string().nullish(),
});
export const VariantExchangeItemSchema: z.ZodType<VariantExchangeItem> =
  VariantExchangeItemSchema_;
export interface VariantExchangeItem
  extends z.infer<typeof VariantExchangeItemSchema_> {}

export const PendingReturnItemSchema_ = z.object({
  id: zExt.objectId(),
  returnableItemId: z.string(),
  expandedProductVariantExchanges: z.array(VariantExchangeItemSchema).nullish(),
  flow: z.any(),
  flowPath: z.array(z.number()).readonly(),
  locationFlow: z.any().nullish(),
  labelFlow: z.any().nullish(),
  multipleChoiceAnswers: z
    .array(z.object({ step: z.number(), answer: z.number() }))
    .readonly(),
  inputAnswers: z
    .array(
      z.object({
        type: createEnum(InputPartType),
        value: z.union([z.string(), z.array(z.string())]),
        message: z.string(),
        fieldId: createEnum(FieldId).nullish(),
        step: z.number().nullish(),
      }),
    )
    .readonly()
    .nullish(),

  exchangeGroup: exchangeGroupSchema.nullish(),
  variantExchangeItem: VariantExchangeItemSchema.nullish(),
  advancedExchangeItems: z.array(NewExchangeItemSchema).nullish(),
  manualReview: z.boolean(),
  manualReviewReason: z.string().nullish(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullish(),
  returnReason: z.string().nullish(),
  returnReasons: z.array(z.string()).default([]),
  rejectMessage: z.string().nullish(),
  reasonCode: z.string().nullish(),
  reasonCodes: z.array(z.string()).default([]),
  returnOptions: z.array(z.any()),
  flatRateShippingFee: z.string().nullish(),
  flatRateShippingFeeCurrencyOverrides: z
    .record(z.string(), z.number())
    .nullish(),
  flatRateShippingFeePerCurrencyAmountsEnabled: z.boolean().nullish(),
  dropoffFees: z
    .array(z.object({ provider: z.enum(dropoffProviderType), fee: z.string() }))
    .nullish(),
  /** The upcharge/downcharge value configured in the return flow */
  labelUpchargeValue: z.string().nullish(),
  /** Type of upcharge: "fixed" for fixed amount, "percentage" for percentage */
  labelUpchargeType: z.enum(["fixed", "percentage"]).nullish(),
  selectedReturnOption: z.any().nullish(),
  restockExternalLocationId: z.string().nullish(),
  quantity: z.number().nullish(),
  metadata: z.record(z.string(), z.any()).nullish(),
  notes: z.string().nullish(),
  merchantImages: z.array(z.string()).nullish(),
  aiDamaged: z.boolean().nullish(),
  aiWrongProduct: z.boolean().nullish(),
  aiReturnReasonsThreadId: zExt.objectId().nullish(),
  posInput: z.object({ restockRequested: z.boolean().nullish() }).nullish(),
  returnTags: z
    .array(z.object({ name: z.string(), color: z.string() }))
    .optional(),
});
export const PendingReturnItemSchema: z.ZodType<PendingReturnItem> =
  PendingReturnItemSchema_;
export interface PendingReturnItem
  extends z.infer<typeof PendingReturnItemSchema_> {}

/**
 * For the frontend to pass an item to add to the draft return. Some fields will be fact checked and filled in on the server, rather than trusting the frontend.
 */
export const AddItemPendingItemSchema = PendingReturnItemSchema_.omit({
  id: true,
  manualReview: true,
  flagged: true,
  returnReason: true,
  returnOptions: true,
  selectedReturnOption: true,
  aiDamaged: true,
  aiWrongProduct: true,
  // Don't pass in the flow from the frontend. It will be set on the server.
  flow: true,
}).extend({ isBundleGroupReturn: z.boolean().nullish() });
export type AddItemPendingItem = z.infer<typeof AddItemPendingItemSchema>;

/** @param returnProductId - The id of the return_ product_, not the product ID (see get-returnable-items.ts) */
export function getReturnableItemId(
  returnProductId: string,
  returnableItemIndex: string,
) {
  return `${returnProductId}-${returnableItemIndex}`;
}

export function parseReturnableItemId(returnableItemId?: string) {
  if (!returnableItemId) {
    return undefined;
  }
  try {
    const [returnProductId, returnableItemIndex] = returnableItemId.split("-");
    return { returnProductId, returnableItemIndex };
  } catch (e) {
    return undefined;
  }
}
